// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { GetInvoicesApiParameter } from "@insite/client-framework/Services/InvoiceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadInvoices from "@insite/client-framework/Store/Data/Invoices/Handlers/LoadInvoices";
import {
    getInvoicesDataView,
    InvoicesDataViewContext,
} from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/UpdateSearchFields";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import qs from "qs";
import * as React from "react";
import { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const getInvoicesParameter = { ...state.pages.invoiceHistory.getInvoicesParameter, showOpenOnly: true };
    return {
        settings: getSettingsCollection(state),
        getInvoicesParameter,
        invoicesDataView: getInvoicesDataView(state, getInvoicesParameter),
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    loadInvoices,
    updateSearchFields,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const StatementsPage = ({
    settings,
    updateSearchFields,
    loadInvoices,
    id,
    invoicesDataView,
    history,
    location,
    getInvoicesParameter,
}: Props) => {
    const firstLoad = React.useRef(false);
    useEffect(() => {
        firstLoad.current = true;
        const pageSizeCookie = getCookie("StatementsPage-PageSize");
        const pageSize = pageSizeCookie ? parseInt(pageSizeCookie, 10) : undefined;
        if (location.search) {
            const getInvoicesApiParameter = parseQueryString<GetInvoicesApiParameter>(location.search);
            if (pageSize) {
                getInvoicesApiParameter.pageSize = pageSize;
            }
            updateSearchFields({ ...getInvoicesApiParameter, type: "Replace" });
        }
    }, []);

    useEffect(() => {
        if (!firstLoad) {
            const queryString = qs.stringify(getInvoicesParameter);
            history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
        }
    }, [getInvoicesParameter]);

    useEffect(() => {
        if (!invoicesDataView.value && !invoicesDataView.isLoading) {
            loadInvoices(getInvoicesParameter);
        }
    });

    return (
        <Page>
            <InvoicesDataViewContext.Provider value={invoicesDataView}>
                <Zone contentId={id} zoneName="Content" />
            </InvoicesDataViewContext.Provider>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(StatementsPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;
